import { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Group, Paper, Stack, Text } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { CommodityCard } from './CommodityCard';
import { freightFormMessages } from './freight-form-messages';
import styles from './HandlingUnitCard.module.css';
import { PACKAGING_TYPE_OPTIONS } from '../../../../ltl/constants';
import { CustomNumberInput, DimensionsInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';
import { applyHandlingUnitPresetToUnit } from '../../../freight-booking-wizard/components/handling-units-step/apply-handling-unit-preset';
import { handlingUnitsStepMessages } from '../../../freight-booking-wizard/components/handling-units-step/handling-units-step-messages';
import { HandlingUnitPresetAutocomplete } from '../../../freight-booking-wizard/components/handling-units-step/HandlingUnitPresetAutocomplete';
import { CalculateDensityButton } from '../../../freight-density/CalculateDensityButton';

import type { DimensionUnit, Dimensions } from '../../../../types/dimensions';
import type { FreightFulfillmentPlanHandlingUnitPreset } from '../../../../types/freight-responses';
import type { FreightPackagingType, LtlCommodity, LtlHandlingUnit } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';
import type { FreightDensityAction } from '../../../freight-density/RecommendedFreightClassModal';

type HandlingUnitCardProps = {
  unit: LtlHandlingUnit;
  index: number;
  isHazmatEnabled?: boolean;
  sellerFreightProviderId?: number;
  displayWeightUnit?: WeightUnit;
  displayDimensionUnit?: DimensionUnit;
  onChange: (updated: LtlHandlingUnit) => void;
  onDensityAction?: (action: FreightDensityAction) => void;
  onRemove?: () => void;
  zIndex?: number;
};

const createEmptyCommodity = (existingCommodities: LtlCommodity[]): LtlCommodity => ({
  id: crypto.randomUUID(),
  description: '',
  freightClass: '',
  nmfcCode: '',
  quantity: 1,
  weight: 0,
  weightUnit: existingCommodities[0]?.weightUnit,
  isHazmat: false,
  hazmatDetails: null,
  hazmatInfo: null,
  insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
});

export const HandlingUnitCard = ({
  unit,
  index,
  isHazmatEnabled,
  sellerFreightProviderId,
  displayWeightUnit,
  displayDimensionUnit,
  onChange,
  onDensityAction,
  onRemove,
  zIndex,
}: HandlingUnitCardProps) => {
  const intl = useIntl();
  const [presetSearchValue, setPresetSearchValue] = useState('');

  const handleHandlingUnitPresetSelect = useCallback(
    (preset: FreightFulfillmentPlanHandlingUnitPreset) => onChange(applyHandlingUnitPresetToUnit(unit, preset)),
    [unit, onChange],
  );

  const handleTypeChange = (val: string | null) => {
    if (val) onChange({ ...unit, packagingType: val as FreightPackagingType });
  };

  const handleQuantityChange = (val: number | string) => {
    const num = typeof val === 'string' ? parseInt(val, 10) || 1 : val;
    onChange({ ...unit, quantity: num });
  };

  const handleStackableChange = (isStackable: boolean) => {
    onChange({ ...unit, isStackable });
  };

  const handleDimensionsChange = (next: Dimensions) => {
    onChange({ ...unit, length: next.length, width: next.width, height: next.height, dimensionUnit: next.unit });
  };

  const handleCommodityChange = (commodityIndex: number, updated: LtlCommodity) => {
    const commodities = [...unit.commodities];
    commodities[commodityIndex] = updated;
    onChange({ ...unit, commodities });
  };

  const handleAddCommodity = () => {
    onChange({ ...unit, commodities: [...unit.commodities, createEmptyCommodity(unit.commodities)] });
  };

  const handleRemoveCommodity = (commodityIndex: number) => {
    onChange({ ...unit, commodities: unit.commodities.filter((_, idx) => idx !== commodityIndex) });
  };

  const dimensionsValue: Dimensions = {
    length: unit.length,
    width: unit.width,
    height: unit.height,
    unit: (unit.dimensionUnit as Dimensions['unit']) ?? 'Inches',
  };

  const packagingTypeData = useMemo(
    () =>
      PACKAGING_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: intl.formatMessage(option.label),
      })),
    [intl],
  );

  return (
    <Paper data-testid={`handling-unit-card-${index}`} p='md' shadow='xs' withBorder>
      <Stack gap='sm'>
        <Group justify='space-between'>
          <Text fw={600}>{intl.formatMessage(freightFormMessages.handlingUnitTitle, { number: index + 1 })}</Text>
          <Group gap='xs'>
            <CalculateDensityButton
              displayDimensionUnit={displayDimensionUnit}
              displayWeightUnit={displayWeightUnit}
              index={index}
              onChange={onChange}
              onDensityAction={onDensityAction}
              sellerFreightProviderId={sellerFreightProviderId}
              unit={unit}
              zIndex={zIndex}
            />
            {onRemove ? (
              <Button color='red' data-testid={`remove-unit-${index}`} onClick={onRemove} size='xs' variant='ghost'>
                <FormattedMessage {...freightFormMessages.removeUnit} />
              </Button>
            ) : null}
          </Group>
        </Group>

        <HandlingUnitPresetAutocomplete
          data-testid={`unit-preset-autocomplete-${index}`}
          label={intl.formatMessage(handlingUnitsStepMessages.handlingUnitPresetLabel)}
          onChange={setPresetSearchValue}
          onHandlingUnitPresetSelect={handleHandlingUnitPresetSelect}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.handlingUnitPresetPlaceholder)}
          value={presetSearchValue}
        />

        <Checkbox
          checked={unit.isStackable}
          data-testid={`unit-stackable-${index}`}
          label={intl.formatMessage(freightFormMessages.stackableLabel)}
          onChange={(event) => handleStackableChange(event.currentTarget.checked)}
        />
        <Group grow>
          <Dropdown
            data={packagingTypeData}
            data-testid={`unit-type-${index}`}
            label={intl.formatMessage(freightFormMessages.unitTypeLabel)}
            onChange={handleTypeChange}
            value={unit.packagingType}
            withAsterisk
          />
          <CustomNumberInput
            allowNegative={false}
            data-testid={`unit-quantity-${index}`}
            label={intl.formatMessage(freightFormMessages.quantityLabel)}
            min={1}
            onChange={handleQuantityChange}
            value={unit.quantity}
            withAsterisk
          />
        </Group>
        <Text size='sm'>
          {intl.formatMessage(freightFormMessages.dimensionsLabel, { unit: dimensionsValue.unit })}
          <span aria-hidden className={styles.requiredAsterisk}>
            &nbsp;*
          </span>
        </Text>
        <DimensionsInput onChange={handleDimensionsChange} value={dimensionsValue} />
        {unit.commodities.map((commodity, cIdx) => (
          <CommodityCard
            commodity={commodity}
            index={cIdx}
            isHazmatEnabled={isHazmatEnabled}
            key={commodity.id}
            onChange={(updated) => handleCommodityChange(cIdx, updated)}
            onRemove={cIdx > 0 ? () => handleRemoveCommodity(cIdx) : undefined}
          />
        ))}
        <Button data-testid={`add-commodity-${index}`} onClick={handleAddCommodity} variant='hyperlink'>
          <FormattedMessage {...freightFormMessages.addCommodity} />
        </Button>
      </Stack>
    </Paper>
  );
};
